import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, ReferenceLine, Cell, LabelList,
} from 'recharts';
import {
  Trophy, AlertTriangle, AlertCircle, ChevronRight, Coins, Star,
  TrendingUp, RefreshCw, Crosshair, Loader2,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

const fmtCurrency = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
const fmtNum = (v) => v == null ? '-' : Number(v).toLocaleString('it-IT');

const METRICS = [
  { key: 'success_fee', label: 'Success Fee', icon: Coins, color: '#10b981', fmt: 'currency' },
  { key: 'nps', label: 'NPS', icon: Star, color: '#a855f7', fmt: 'number', max: 10 },
  { key: 'upsells', label: 'Up-Sell', icon: TrendingUp, color: '#f97316', fmt: 'count_amount' },
  { key: 'renewals', label: 'Rinnovi', icon: RefreshCw, color: '#3b82f6', fmt: 'count_amount' },
];

const TRIGGER_LABEL = {
  sf_decline: 'SF in calo',
  sf_type_decline: 'SF per tipologia in calo',
  low_renewal_rate: '% Rinnovo basso',
  nps_decline: 'NPS in calo',
  nps_critical: 'NPS critico',
};

const KIND_LABEL = {
  global: 'Globale',
  pm: 'Project Manager',
  bu: 'Business Unit',
  product: 'Prodotto',
};

function SeverityBadge({ severity }) {
  if (severity === 'critical') {
    return (
      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border border-red-300 text-[10px] px-1.5 py-0">
        <AlertCircle className="w-3 h-3 mr-1" /> Critico
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border border-amber-300 text-[10px] px-1.5 py-0">
      <AlertTriangle className="w-3 h-3 mr-1" /> Attenzione
    </Badge>
  );
}

function AlertRow({ alert }) {
  return (
    <div className="flex items-start gap-2 py-2 border-b border-gray-100 last:border-0" data-testid={`alert-${alert.trigger}-${alert.kind}`}>
      <div className="pt-0.5"><SeverityBadge severity={alert.severity} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{alert.title}</div>
        <div className="text-xs text-gray-500 truncate">
          <span className="text-gray-700">{alert.label}</span>
          {alert.description ? <span className="mx-1">·</span> : null}
          {alert.description}
        </div>
      </div>
    </div>
  );
}

function AlertsBlock({ title, icon: Icon, alerts, emptyHint }) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          {title}
          <span className="text-xs text-gray-400 font-normal">({alerts.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {alerts.length === 0 ? (
          <p className="text-xs text-gray-400 py-4 text-center">{emptyHint || 'Nessun segnale.'}</p>
        ) : (
          <div className="space-y-0">
            {alerts.map((a, idx) => <AlertRow key={`${a.trigger}-${a.id || a.label}-${idx}`} alert={a} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RankingBar({ row, max, metric, isCurrentUser }) {
  const pct = max > 0 ? Math.max(2, Math.min(100, (row.value / max) * 100)) : 0;
  const valueLabel = (() => {
    if (metric.fmt === 'currency') return fmtCurrency(row.value);
    if (metric.fmt === 'number') return row.value != null && row.value !== 0 ? row.value.toFixed(1) : '-';
    if (metric.fmt === 'count_amount') return (
      <span>
        <b>{fmtNum(row.value)}</b>
        <span className="text-gray-400 ml-1.5 text-[11px]">{fmtCurrency(row.amount)}</span>
      </span>
    );
    return fmtNum(row.value);
  })();
  return (
    <div className={`flex items-center gap-3 py-1.5 px-2 rounded ${isCurrentUser ? 'bg-orange-50' : ''}`} data-testid={`ranking-row-${row.pm_id}`}>
      <div className="w-40 text-xs truncate flex items-center gap-1.5" title={row.pm_name}>
        {isCurrentUser && <span className="text-orange-500">●</span>}
        <span className={isCurrentUser ? 'font-semibold text-orange-700' : 'text-gray-800'}>{row.pm_name}</span>
      </div>
      <div className="flex-1 relative h-6 bg-gray-100 rounded">
        <div
          className="absolute inset-y-0 left-0 rounded transition-all"
          style={{ width: `${pct}%`, backgroundColor: metric.color, opacity: row.value > 0 ? 0.85 : 0.2 }}
        />
      </div>
      <div className="w-32 text-right text-xs">{valueLabel}</div>
    </div>
  );
}

function QuadrantsScatter({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-xs text-gray-400 py-8 text-center">Dati insufficienti per i quadranti.</p>;
  }
  const pts = data.filter(d => d.sf != null && d.nps != null);
  if (pts.length === 0) {
    return <p className="text-xs text-gray-400 py-8 text-center">Servono almeno NPS e SF per i PM nel periodo.</p>;
  }
  // True median of SF for the vertical boundary (was midpoint = max/2)
  const sortedSf = [...pts.map(d => d.sf)].sort((a, b) => a - b);
  const mid = Math.floor(sortedSf.length / 2);
  const sfMedian = sortedSf.length % 2 === 0
    ? (sortedSf[mid - 1] + sortedSf[mid]) / 2
    : sortedSf[mid];
  const npsMid = 7;
  const ptsWithQuad = pts.map(d => {
    const qHigh = d.nps >= npsMid;
    const qRich = d.sf >= sfMedian;
    let label = '';
    let color = '#6b7280';
    if (qHigh && qRich) { label = 'Eccellente'; color = '#10b981'; }
    else if (qRich && !qHigh) { label = 'Da supportare'; color = '#f97316'; }
    else if (!qRich && qHigh) { label = 'A rischio'; color = '#3b82f6'; }
    else { label = 'Critico'; color = '#ef4444'; }
    return { ...d, label, color };
  });
  const quadrantColors = { Eccellente: '#10b981', 'Da supportare': '#f97316', 'A rischio': '#3b82f6', Critico: '#ef4444' };
  const groupCounts = Object.fromEntries(
    Object.keys(quadrantColors).map(k => [k, ptsWithQuad.filter(p => p.label === k).length])
  );
  return (
    <div className="space-y-3" data-testid="quadrants-scatter">
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis type="number" dataKey="sf" name="Success Fee" tickFormatter={fmtCurrency} stroke="#6b7280" fontSize={11}
            label={{ value: 'Success Fee (€)', position: 'insideBottom', offset: -5, fill: '#6b7280', fontSize: 11 }} />
          <YAxis type="number" dataKey="nps" name="NPS" domain={[0, 10]} stroke="#6b7280" fontSize={11}
            label={{ value: 'NPS Medio', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }} />
          <ReferenceLine x={sfMedian} stroke="#9ca3af" strokeDasharray="4 4" />
          <ReferenceLine y={npsMid} stroke="#9ca3af" strokeDasharray="4 4" />
          <RTooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              return (
                <div className="bg-white border rounded shadow p-2 text-xs">
                  <div className="font-medium">{p.pm_name}</div>
                  <div className="text-gray-500">SF: {fmtCurrency(p.sf)} · NPS: {p.nps?.toFixed(1)}</div>
                  <div className="mt-1"><Badge style={{ backgroundColor: p.color, color: 'white' }} className="text-[10px]">{p.label}</Badge></div>
                </div>
              );
            }}
          />
          {/* Single Scatter series with per-point Cell color: ensures tooltip resolves to the correct point */}
          <Scatter data={ptsWithQuad}>
            {ptsWithQuad.map((entry, idx) => (
              <Cell key={`c-${idx}`} fill={entry.color} />
            ))}
            <LabelList dataKey="pm_name" position="top" style={{ fontSize: 10, fill: '#374151' }} />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        {Object.entries(quadrantColors).map(([name, color]) => (
          <div key={name} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-gray-600">{name}</span>
            <span className="text-gray-400">({groupCounts[name] || 0})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PMRankingInsights({ filters }) {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);
  const isSupervisor = hasRole(['supervisor']);
  const showInsights = isAdmin || isSupervisor;
  
  const [metric, setMetric] = useState(() => {
    try { return localStorage.getItem('pm_ranking_metric') || 'success_fee'; }
    catch { return 'success_fee'; }
  });
  useEffect(() => {
    try { localStorage.setItem('pm_ranking_metric', metric); } catch { /* ignore */ }
  }, [metric]);
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters?.start_date) params.start_date = filters.start_date;
        if (filters?.end_date) params.end_date = filters.end_date;
        if (filters?.pm_id) params.pm_id = filters.pm_id;
        if (filters?.business_unit_id) params.business_unit_id = filters.business_unit_id;
        if (filters?.client_id) params.client_id = filters.client_id;
        if (filters?.project_id) params.product_id = filters.product_id;
        const res = await api.get('/dashboard/pm-ranking-insights', { params });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Ranking/Insights load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters]);
  
  const currentMetric = METRICS.find(m => m.key === metric) || METRICS[0];
  const rankingRows = (data?.ranking?.[metric] || []).filter(r => (r.value || 0) > 0 || (r.amount || 0) > 0);
  const maxValue = useMemo(() => Math.max(0, ...rankingRows.map(r => r.value || 0)), [rankingRows]);
  
  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* Classifica */}
        <Card data-testid="pm-ranking-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                Classifica Project Manager
                <span className="text-xs text-gray-400 font-normal">· {data?.period_label || ''}</span>
              </CardTitle>
              <div className="flex gap-1">
                {METRICS.map(m => {
                  const Icon = m.icon;
                  const active = m.key === metric;
                  return (
                    <Button
                      key={m.key}
                      size="sm"
                      variant={active ? 'default' : 'outline'}
                      className={active ? 'h-7 text-xs bg-gray-900 hover:bg-gray-800' : 'h-7 text-xs'}
                      onClick={() => setMetric(m.key)}
                      data-testid={`ranking-metric-${m.key}`}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {m.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
            ) : rankingRows.length === 0 ? (
              <p className="text-xs text-gray-400 py-6 text-center">Nessun PM con dati per <b>{currentMetric.label}</b> nel periodo.</p>
            ) : (
              <div className="space-y-0.5">
                {rankingRows.map(r => (
                  <RankingBar key={r.pm_id} row={r} max={maxValue} metric={currentMetric} isCurrentUser={user?.id === r.pm_id} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Insights (Admin + Supervisor) */}
        {showInsights && (
          <>
            <div className="flex items-center justify-between mt-5">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Performance Insights
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-gray-400 cursor-help">ⓘ</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    Confronto vs periodo precedente di pari lunghezza ({data?.previous_period_label}).
                    Soglie: SF -30% critico / -20% attenzione · NPS &lt;6 critico · NPS -1.5pt attenzione · % Rinnovo &lt;30% attenzione.
                  </TooltipContent>
                </Tooltip>
              </h2>
            </div>
            
            {loading ? (
              <Card><CardContent className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></CardContent></Card>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="insights-alerts-grid">
                  <AlertsBlock
                    title="Globale"
                    icon={AlertCircle}
                    alerts={data?.insights?.alerts?.global || []}
                    emptyHint="Tutto in ordine a livello aggregato."
                  />
                  <AlertsBlock
                    title="Per PM"
                    icon={ChevronRight}
                    alerts={data?.insights?.alerts?.per_pm || []}
                    emptyHint="Nessun PM in segnale."
                  />
                  <AlertsBlock
                    title="Per Area"
                    icon={ChevronRight}
                    alerts={data?.insights?.alerts?.per_area || []}
                    emptyHint="Nessuna area in segnale."
                  />
                </div>
                
                {/* Quadranti SF vs NPS */}
                <Card data-testid="insights-quadrants-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Crosshair className="w-4 h-4 text-indigo-500" />
                      Quadranti SF vs NPS
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-gray-400 cursor-help">ⓘ</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          Scatter dei PM su Success Fee (X) e NPS Medio (Y). I quadranti dividono per SF mediana e NPS=7.
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <QuadrantsScatter data={data?.insights?.quadrants || []} />
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
